import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import dayjs from "dayjs";
import { formatWord } from "@app/utils/post";
import ProfileButton from "./ProfileButton";
import type { User, LinkMetadata } from "@prisma/client";
import useUser from "@app/zustand/user";
import { CommentType } from "@api/src/types/post";
import { PostAction } from "@app/types/post";
import ThumbUp from "@app/icons/Thumb";
import PostCardUserPopover from "./PostCardUserPopover";
import { PostResponse } from "@api/src/types/responses";

type CommentProps = {
  comment: CommentType;
  handlePostAction: ({ action, content }: PostAction) => Promise<void>;
};

export default function Comment({ comment, handlePostAction }: CommentProps) {
  const { id, content, createdAt, usersThatLiked, roomId, authorId } = comment;
  const author = comment.author as User;
  const link = comment.linkMetadata as LinkMetadata;
  const [showReply, setShowReply] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);
  const me = useUser((state) => state.user!);
  const commentFormRef = useRef<HTMLFormElement>(null);

  const isLiked = useMemo(() => !!usersThatLiked.find((user) => user.userId === me.id), [usersThatLiked, me.id]);
  const [isLiking, setIsLiking] = useState(false);
  const [isUnliking, setIsUnliking] = useState(false);
  const showAsLiked = !isUnliking && (isLiking || isLiked);
  const showNumberOfLikes = usersThatLiked.length + (!isLiked && isLiking ? 1 : 0) - (isLiked && isUnliking ? 1 : 0);

  const handleSubmitComment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsCommenting(true);

    const formData = new FormData(event.currentTarget);

    await handlePostAction({
      action: "comment",
      content: formData.get("content") as string,
    });
    setShowReply(false);
    setIsCommenting(false);
    commentFormRef.current?.reset();
  };

  const handleCommentAction = async ({ action, content }: PostAction) => {
    // Set the appropriate loading state
    switch (action) {
      case "like":
        setIsLiking(true);
        setIsUnliking(false);
        break;
      case "unlike":
        setIsUnliking(true);
        setIsLiking(false);
        break;
    }

    try {
      await fetch(`${import.meta.env.VITE_API_URL}/post/${id}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action,
          postId: id,
          roomId,
          authorId,
          content,
        }),
      })
        .then(async (response) => response.json() as Promise<PostResponse>)
        .then((response) => {
          if (!response.ok) {
            console.error("Failed to perform post action:", response, {
              action,
              postId: comment.id,
              roomId: comment.roomId,
              authorId: comment.author.id,
              content,
            });
            setIsLiking(false);
            setIsUnliking(false);
          }
        });
    } catch (error) {
      console.error("Failed to perform post action:", error);
      setIsLiking(false);
      setIsUnliking(false);
    }
  };

  return (
    <aside className="mt-2 flex w-full items-start px-4">
      <ProfileButton user={author} />
      <div className="ml-2 w-full overflow-hidden rounded bg-gray-100">
        <div className="flex justify-between px-2 pt-2">
          <div>
            <Link to={`/profile/${author.plausible_id}`} className="text-sm ">
              <span className="font-semibold capitalize">
                {author?.firstName} {author?.lastName}
              </span>
              {/* <span className="text-gray-500"> • 1st</span> */}
            </Link>
            <div className="text-xs text-gray-500 ">
              {author.status_pro} - {author.specialite}
            </div>
          </div>
          <div className="flex text-right text-xs text-gray-500">
            <span className="inline-block">{dayjs().to(dayjs(createdAt))}</span>
          </div>
        </div>
        <p className="overflow-hidden text-ellipsis px-2 py-2 text-sm leading-normal text-gray-800 md:leading-relaxed">
          {content.split("\n").map((sentence: string, i: number) => (
            <React.Fragment key={i}>
              {sentence.split(" ").map(formatWord)}
              <br />
            </React.Fragment>
          ))}
        </p>
        {!!link && (
          <a className="mt-2 flex w-full flex-col overflow-hidden rounded" href={link.url} target="_blank">
            <img
              src={link.image!}
              alt={link.description!}
              loading="lazy"
              className="flex-0 aspect-link w-full object-cover"
            />
            <div className="flex flex-col bg-gray-100 px-4 py-3">
              <span className="font-sansSerif text-xs font-normal uppercase opacity-50">{link.publisher}</span>
              <span className="font-sansSerif truncate font-semibold ">{link.title}</span>
            </div>
          </a>
        )}
        <div className="flex justify-between">
          <form
            method="post"
            onSubmit={(e) => {
              e.preventDefault();
              handleCommentAction({ action: showAsLiked ? "unlike" : "like" });
            }}
          >
            <div className="flex items-center pr-4 pl-1 text-xs text-sky-900">
              <button type="submit">
                <ThumbUp fill={showAsLiked ? "currentColor" : "none"} />
              </button>
              {showNumberOfLikes > 0 ? (
                <PostCardUserPopover
                  showNumberOfLikes={showNumberOfLikes}
                  postId={id}
                  usersThatLiked={usersThatLiked.map((l) => l.user)}
                />
              ) : (
                <span className="px-2">{showNumberOfLikes}</span>
              )}
            </div>
          </form>
          <button className="p-1 text-xs text-sky-900" type="button" onClick={() => setShowReply(true)}>
            Répondre
          </button>
        </div>
        {!!showReply && (
          <>
            <form onSubmit={handleSubmitComment} ref={commentFormRef}>
              <div className="mt-3 flex items-start border-t border-sky-900 border-opacity-10 px-4 pt-2 text-xs text-sky-900">
                <ProfileButton user={me} />
                <div className="ml-1 flex w-full flex-col items-start">
                  <textarea
                    name="content"
                    autoFocus
                    onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                    placeholder="Commenter..."
                    className="min-h-9 w-full flex-grow rounded-lg border border-sky-900 border-opacity-30 px-4 py-2 text-left text-sky-900"
                    defaultValue={`@${author.firstName} `}
                  />
                  <button
                    type="submit"
                    className="ml-auto mt-1 inline-block rounded-full border-none bg-sky-800 px-2 py-1 text-white"
                  >
                    {isCommenting ? "En cours..." : "Publier"}
                  </button>
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </aside>
  );
}
